import { Close } from '@mui/icons-material';
import { lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@ifs-gcp/components';

import { useLeaveAudit } from '@/hooks/useLeaveAudit';
import { useAuditStore } from '@/store/auditStore';
import { checkUnsavedChanges } from '@/utils/unsavedChanges';

const LazyCloseAuditModal = lazy(() => import('@/components/close-audit-modal/close-audit-modal'));

type CloseAuditActionProps = {
  auditCaseUuid: string;
  onSaveAudit: (successCallback?: () => void) => void;
};

export const CloseAuditAction = ({ onSaveAudit, auditCaseUuid }: CloseAuditActionProps) => {
  const { t } = useTranslation();
  const [isCloseAuditModalOpen, setIsCloseAuditModalOpen] = useState(false);
  const handleLeaveAudit = useLeaveAudit(auditCaseUuid);
  const audit = useAuditStore((state) => state.audit);
  const lastSavedAuditBtoa = useAuditStore((state) => state.lastSavedAuditBtoa);
  const isActionPending = useAuditStore((state) => state.isActionPending);

  const handleOpenCloseSelfAssessment = () => {
    setIsCloseAuditModalOpen(true);
  };

  const handleCloseCloseSelfAssessment = () => {
    setIsCloseAuditModalOpen(false);
  };

  const handleSaveAndCloseCloseModal = () => {
    onSaveAudit(() => {
      handleLeaveAudit();
    });
  };

  const handleCloseAudit = () => {
    const hasUnsavedChanges = checkUnsavedChanges(lastSavedAuditBtoa, audit);

    if (hasUnsavedChanges) {
      handleOpenCloseSelfAssessment();
      return;
    }

    handleLeaveAudit();
  };

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<Close />}
        onClick={handleCloseAudit}
        disabled={isActionPending}
      >
        {t('actions.close')}
      </Button>

      {isCloseAuditModalOpen && (
        <LazyCloseAuditModal
          onSave={handleSaveAndCloseCloseModal}
          onClose={handleCloseCloseSelfAssessment}
          onCancel={handleLeaveAudit}
        />
      )}
    </>
  );
};
